import { cn } from "@simplicity/ui";
import { useMemo, useState } from "react";
import type { ConnectorRun } from "../../types";
import type { ProjectDayGroup, ProjectGroup, StatusColumn } from "../RunsTable.utils";
import {
  buildStatusColumn,
  flattenConnectorRuns,
  formatDay,
  formatDuration,
  STATUS_COLORS,
  sumDurationAndPages,
  sumRecords,
} from "../RunsTable.utils";
import { ConnectorRunRow } from "./ConnectorRunRow";

function StatusChart({ columns }: { columns: StatusColumn[] }) {
  if (columns.length === 0) return null;
  return (
    <span className="status-chart">
      {columns.map((col, i) => (
        <span key={i} className="status-chart-col" title={col.label}>
          {col.segments.map((s) => (
            <span
              key={s.status}
              className={cn("status-chart-seg", `day-bar-${s.variant}`)}
              // Dynamic-from-JS: stacked-bar segment height derived from runtime
              // status distribution. Keep inline — cannot be a CSS class.
              style={{ height: `${s.pct}%` }}
            />
          ))}
        </span>
      ))}
    </span>
  );
}

function DayRunDots({
  connectorRuns,
}: {
  connectorRuns: { connectorRun: ConnectorRun; displayName: string }[];
}) {
  const sorted = [...connectorRuns].sort(
    (a, b) =>
      new Date(a.connectorRun.startedAt).getTime() - new Date(b.connectorRun.startedAt).getTime(),
  );
  return (
    <span className="day-run-dots">
      {sorted.map(({ connectorRun, displayName }) => {
        const { variant } = STATUS_COLORS[connectorRun.status];
        return (
          <span
            key={connectorRun.id}
            className={cn("day-run-dot", `day-bar-${variant}`)}
            title={`${displayName}: ${connectorRun.status}`}
          />
        );
      })}
    </span>
  );
}

function DayRow({
  group,
  autoExpandConnectorId,
}: {
  group: ProjectDayGroup;
  autoExpandConnectorId?: number | null;
}) {
  const [isExpanded, setIsExpanded] = useState(() => !!autoExpandConnectorId);
  const [expandedConnectors, setExpandedConnectors] = useState<Set<number>>(() =>
    autoExpandConnectorId ? new Set([autoExpandConnectorId]) : new Set(),
  );

  const toggleConnector = (connectorRunId: number) => {
    setExpandedConnectors((prev) => {
      const next = new Set(prev);
      if (next.has(connectorRunId)) next.delete(connectorRunId);
      else next.add(connectorRunId);
      return next;
    });
  };

  const connectorRuns = useMemo(() => flattenConnectorRuns(group.runs), [group.runs]);
  const { totalInserted, totalFetched, totalSkipped } = useMemo(
    () => sumRecords(group.runs),
    [group.runs],
  );
  const { totalSeconds, totalPages } = useMemo(
    () => sumDurationAndPages(connectorRuns),
    [connectorRuns],
  );

  return (
    <>
      <tr className="detail-row-day" onClick={() => setIsExpanded((prev) => !prev)}>
        <td className="cell-name">
          <span className="indent-day">
            <span className="expand-icon">{isExpanded ? "▼" : "▶"}</span>
            {formatDay(group.runs[0].startedAt)}
          </span>
        </td>
        <td className="cell-day"></td>
        <td>–</td>
        <td className="cell-numeric">{totalPages > 0 ? totalPages.toLocaleString() : "–"}</td>
        <td className="cell-numeric">{formatDuration(totalSeconds || null)}</td>
        <td>
          <DayRunDots connectorRuns={connectorRuns} />
        </td>
        <td></td>
        <td className="cell-numeric">{connectorRuns.length}</td>
        <td className="cell-numeric">{totalInserted.toLocaleString()}</td>
        <td className="cell-numeric">{totalFetched.toLocaleString()}</td>
        <td className="cell-numeric">{totalSkipped.toLocaleString()}</td>
        <td></td>
      </tr>
      {isExpanded &&
        connectorRuns.map(({ connectorRun, displayName }) => (
          <ConnectorRunRow
            key={connectorRun.id}
            connectorRun={connectorRun}
            displayName={displayName}
            isExpanded={expandedConnectors.has(connectorRun.id)}
            onToggle={() => toggleConnector(connectorRun.id)}
          />
        ))}
    </>
  );
}

function DayStatusChart({ days }: { days: ProjectDayGroup[] }) {
  const sorted = [...days].sort((a, b) => a.dateKey.localeCompare(b.dateKey));
  const columns = sorted.map((day) => {
    const connectorRuns = flattenConnectorRuns(day.runs);
    const statuses = connectorRuns.map(({ connectorRun }) => connectorRun.status);
    return buildStatusColumn(
      statuses,
      `${formatDay(day.runs[0].startedAt)}: ${statuses.length} runs`,
    );
  });
  return <StatusChart columns={columns} />;
}

export function ProjectRow({
  project,
  autoExpandConnectorId,
  defaultExpanded = false,
}: {
  project: ProjectGroup;
  autoExpandConnectorId?: number | null;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(() => defaultExpanded || !!autoExpandConnectorId);

  const allRuns = useMemo(() => project.days.flatMap((d) => d.runs), [project.days]);
  const allConnectorRuns = useMemo(() => flattenConnectorRuns(allRuns), [allRuns]);
  const { totalInserted, totalFetched, totalSkipped } = useMemo(
    () => sumRecords(allRuns),
    [allRuns],
  );
  const { totalSeconds, totalPages } = useMemo(
    () => sumDurationAndPages(allConnectorRuns),
    [allConnectorRuns],
  );

  return (
    <>
      <tr className="detail-row-project-day" onClick={() => setIsExpanded((prev) => !prev)}>
        <td className="cell-name">
          <span className="indent-project">
            <span className="expand-icon">{isExpanded ? "▼" : "▶"}</span>
            {project.projectName}
          </span>
        </td>
        <td className="cell-day">{project.days.length}d</td>
        <td>–</td>
        <td className="cell-numeric">{totalPages > 0 ? totalPages.toLocaleString() : "–"}</td>
        <td className="cell-numeric">{formatDuration(totalSeconds || null)}</td>
        <td>
          <DayStatusChart days={project.days} />
        </td>
        <td></td>
        <td className="cell-numeric">{allConnectorRuns.length}</td>
        <td className="cell-numeric">{totalInserted.toLocaleString()}</td>
        <td className="cell-numeric">{totalFetched.toLocaleString()}</td>
        <td className="cell-numeric">{totalSkipped.toLocaleString()}</td>
        <td></td>
      </tr>
      {isExpanded &&
        project.days.map((day) => (
          <DayRow key={day.dateKey} group={day} autoExpandConnectorId={autoExpandConnectorId} />
        ))}
    </>
  );
}
